import { DataContainer } from "@app/classes/infrastructure/datacontainer";
import { DataContainerService } from "@app/classes/infrastructure/datacontainer.service";
import { RequestTypes } from "@app/classes/infrastructure/enums";
import { IPersistable } from "@app/classes/infrastructure/IPersistable";
import { PayloadPacketFacade } from "@app/classes/infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "@app/classes/infrastructure/transportdata";
import { ValidationResultAccumulator } from "@app/classes/infrastructure/validationresultaccumulator";
import { IdProvider } from "@app/services/idprovider.service";
import { ServerCommunicatorService } from "@app/services/server-communicator.service";
import { UIUtils } from "@app/services/uiutils.service";
import { Utils } from "@app/services/utils.service";
import { isNullOrUndefined } from "src/tools";
import { AboutFetchRequest } from "./aboutfetchrequest";


export class AboutProps {

  public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public description_en: string = '';
  public description_mr: string = '';

  public readonly is_newly_created: boolean = false;


  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new AboutProps(true);
  }
}

export class About implements IPersistable<About> {
  public static readonly mastertableName: string = 'about_master';

  private constructor(public readonly p: AboutProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): About {
    let newAbout: AboutProps = Utils.GetInstance().DeepCopy(this.p);
    return About.CreateInstance(newAbout, true);
  }

  public static CreateNewInstance() {
    return new About(AboutProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new About(data as AboutProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');
    if (!this.p.description_en || this.p.description_en.trim() == '') {
      vra.add('description_en', 'DescriptionEn cannot be blank. ');
    }
    if (!this.p.description_mr || this.p.description_mr.trim() == '') {
      vra.add('description_mr', 'DescriptionMr cannot be blank. ');
    }
    // if (this.p.AboutRef <= 0) vra.add('AboutRef', 'About cannot be blank. ');
    // if (this.p.CityRef <= 0) vra.add('CityRef', 'City cannot be blank. ');
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, About.mastertableName, this.p);
  }

  private static m_currentInstance: About = About.CreateNewInstance();

  public static GetCurrentInstance() {
    return About.m_currentInstance;
  }

  public static SetCurrentInstance(value: About) {
    About.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): About {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, About.mastertableName)) {
      for (let data of dcs.GetCollection(td.MainData, About.mastertableName)!.Entries) {
        return About.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): About[] {
    let result: About[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, About.mastertableName)) {
      let coll = dcs.GetCollection(cont, About.mastertableName)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(About.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): About[] {
    return About.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: AboutFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequestWithoutLoginToken(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }
  public static async FetchTransportDataWithoutLoginToken(req: AboutFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequestWithoutLoginToken(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  // Function created For Trasaction Service filters 
  public static async FetchTransportDataForTransactionFilter(req: AboutFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new AboutFetchRequest();
    req.state_ids.push(id);

    let tdResponse = await About.FetchTransportData(req, errorHandler) as TransportData;
    return About.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: AboutFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await About.FetchTransportData(req, errorHandler) as TransportData;
    return About.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new AboutFetchRequest();
    let tdResponse = await About.FetchTransportData(req, errorHandler) as TransportData;
    return About.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListWithoutLoginToken(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new AboutFetchRequest();
    let tdResponse = await About.FetchTransportDataWithoutLoginToken(req, errorHandler) as TransportData;
    return About.ListFromTransportData(tdResponse);
  }


  public static async FetchEntireListByStateRef(state_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new AboutFetchRequest();
    req.state_ids.push(state_id)
    let tdResponse = await About.FetchTransportData(req, errorHandler) as TransportData;
    return About.ListFromTransportData(tdResponse);
  }

  // get all asha for Trasaction filter service file 
  public static async FetchEntireListForTransactionFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new AboutFetchRequest();
    let tdResponse = await About.FetchTransportDataForTransactionFilter(req, errorHandler) as TransportData;
    return About.ListFromTransportData(tdResponse);
  }


  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
